import { memo } from 'react'

import { Spinner } from '@ui/components/loaders'
import { DynamicFormProps, DynamicFormValues, Template } from '@ui/interfaces'
import { useLogoPathBySlug } from '@ui/lib/hooks/useLogoPathBySlug'
import AwsS3 from '@ui/pages/pipelinesWorkflow/components/DestinationForms/AwsS3'
import Chronosphere from '@ui/pages/pipelinesWorkflow/components/DestinationForms/Chronosphere'
import DatadogLogs from '@ui/pages/pipelinesWorkflow/components/DestinationForms/DatadogLogs'
import Elasticsearch from '@ui/pages/pipelinesWorkflow/components/DestinationForms/Elasticsearch'
import GenericOtel from '@ui/pages/pipelinesWorkflow/components/DestinationForms/GenericOtel'
import GrafanaCloud from '@ui/pages/pipelinesWorkflow/components/DestinationForms/GrafanaCloud'
import Honeycomb from '@ui/pages/pipelinesWorkflow/components/DestinationForms/Honeycomb'
import HttpJsonPost from '@ui/pages/pipelinesWorkflow/components/DestinationForms/HttpJsonPost'
import NewRelicAgent from '@ui/pages/pipelinesWorkflow/components/DestinationForms/NewRelicAgent'
import NewRelicOtel from '@ui/pages/pipelinesWorkflow/components/DestinationForms/NewRelicOtel'
import Sentry from '@ui/pages/pipelinesWorkflow/components/DestinationForms/Sentry'
import SigNozOtel from '@ui/pages/pipelinesWorkflow/components/DestinationForms/SigNozOtel'
import Uptrace from '@ui/pages/pipelinesWorkflow/components/DestinationForms/Uptrace'

type FormType = {
  [key: string]: React.FC<DynamicFormProps>
}

export const formComponents: FormType = {
  'new-relic-agent': NewRelicAgent,
  'new-relic-otel': NewRelicOtel,
  'http-json-post': HttpJsonPost,
  'aws-s3': AwsS3,
  signoz: SigNozOtel,
  otel: GenericOtel,
  'datadog-logs': DatadogLogs,
  'grafana-cloud': GrafanaCloud,
  honeycomb: Honeycomb,
  sentry: Sentry,
  uptrace: Uptrace,
  elasticsearch: Elasticsearch,
  chronosphere: Chronosphere,
}

interface ExporterFormProps extends Pick<DynamicFormProps, 'additionalInputs'> {
  template: Template | null
  config: DynamicFormValues
  cancel?: () => void
  saveConfig: (values: DynamicFormValues) => void
}

export const ExporterForm = memo(({ cancel, template, config, saveConfig, additionalInputs }: ExporterFormProps) => {
  const Component = template?.slug ? formComponents[template.slug] : undefined
  const logoPathBySlug = useLogoPathBySlug([template?.slug])

  if (!template) {
    return <div>Something went wrong</div>
  }

  return (
    <div className="rounded-b-md bg-white p-4">
      <div className="mb-2 flex w-full text-xl font-semibold text-gray-900">
        <div className="flex h-11 w-11 flex-none items-center justify-center">
          <img src={logoPathBySlug[template.slug]} className="h-full w-full text-gray-600" aria-hidden="true" />
        </div>
        <div className="my-auto pl-2">{template.name}</div>
      </div>
      <div className="space-y-6 pt-10 sm:space-y-5">
        {Component ? (
          <Component config={config} cancelClick={cancel} saveClick={saveConfig} additionalInputs={additionalInputs} />
        ) : (
          <Spinner />
        )}
      </div>
    </div>
  )
})
